import architecturePreview from '../assets/featured-architecture.png'
import blogPreview from '../assets/featured-blog.png'
import caseStudiesPreview from '../assets/featured-case-studies.png'
import myWorksPreview from '../assets/featured-myworks.png'
import fdrpReviewImage from '../assets/review/fdrp.webp'

export type FeaturedItem = {
    id: string
    label: string
    title: string
    description: string
    endpoint: string
    linkLabel: string
    image: string
    imageAlt: string
}

export type AboutMetric = {
    label: string
    value: string
}

export type SkillGroup = {
    title: string
    items: string[]
}

export type ExperienceItem = {
    company: string
    role: string
    timeline: string
    summary: string
    highlights: string[]
}

export type PageCard = {
    id: string
    name: string
    description: string
    duration: string
    role: string
    teamSize: string
    status: string
    stack: string[]
    impact: string
    link?: string
}

export type DeepDiveBlock = {
    label: string
    items: string[]
}

export type PageDeepDive = {
    id: string
    name: string
    tagline: string
    overview: string
    problem: string
    decisions: string[]
    results: string[]
    blocks: DeepDiveBlock[]
}

export type PortfolioPageContent = {
    eyebrow: string
    title: string
    intro: string
    purpose: string
    cardLabel: string
    detailLabel: string
    cards: PageCard[]
    deepDives: PageDeepDive[]
}

export const FEATURED_ITEMS: FeaturedItem[] = [
    {
        id: 'myworks',
        label: 'My Works',
        title: 'My Works',
        description: 'A focused selection of product, AI, and engineering work showing what was built, why it mattered, and where the technical depth lives.',
        endpoint: '/myworks',
        linkLabel: 'view my works',
        image: myWorksPreview,
        imageAlt: 'Preview collage for My Works projects',
    },
    {
        id: 'system-architecture-design',
        label: 'System / Architecture Design',
        title: 'System / Architecture Design',
        description: 'System thinking across architecture, data flow, technical tradeoffs, and the decisions behind scalable product delivery.',
        endpoint: '/system-architecture-design',
        linkLabel: 'explore architecture design',
        image: architecturePreview,
        imageAlt: 'Diagram-style preview for system and architecture design',
    },
    {
        id: 'case-studies',
        label: 'Case Studies',
        title: 'Case Studies',
        description: 'Detailed project narratives covering the problem, implementation approach, engineering decisions, and measurable outcomes.',
        endpoint: '/case-studies',
        linkLabel: 'read case studies',
        image: caseStudiesPreview,
        imageAlt: 'Case study page preview with charts and content blocks',
    },
    {
        id: 'blog',
        label: 'Blog',
        title: 'Blog',
        description: 'Engineering writing on frontend performance, realtime systems, AI product workflows, and lessons from shipped work.',
        endpoint: 'https://medium.com/@tuhinkairi',
        linkLabel: 'visit the blog',
        image: blogPreview,
        imageAlt: 'Editorial style preview for blog content',
    },
]

export const ABOUT_PAGE_CONTENT = {
    eyebrow: 'Detailed About',
    title: 'AI-augmented full-stack engineer building fast, scalable systems with product-minded execution.',
    intro: 'Tuhin Kairi works across React, Next.js, Node.js, Python, cloud tooling, and AI-driven workflows to build web products that are both technically strong and operationally useful. His portfolio spans high-performance publishing platforms, real-time apps, AI interview systems, ML prototypes, and enterprise dashboards.',
    story: [{
        key: 'story-1',
        image:"",
        type:"Person",
        title:"Susma Computers",
        description:'His profile stands out because he bridges interface quality, backend systems, and practical architecture decisions. Instead of treating frontend, backend, DevOps, and AI as separate worlds, he connects them to improve performance, reduce latency, and make complex products easier to operate.'},
        {
        key: 'story-2',
        image:fdrpReviewImage,
        type:"Company",
        title:"FDRP Journals",
        description:'That shows up in the outcomes: Lighthouse scores above 98, SEO at 100, API latency reduced by about 35 percent, productivity gains on dashboard workflows, and stronger retention on AI-driven interview products. The website now organizes that work into a format that is easier to scan first and explore deeply afterward.'},
    ],
    metrics: [
        { label: 'Experience', value: '2+ years' },
        { label: 'Lighthouse Peak', value: '98+' },
        { label: 'SEO', value: '100' },
        { label: 'API Latency Cut', value: '35%' },
        { label: 'Workflow Gain', value: '60%' },
        { label: 'Retention Lift', value: '18%' },
    ] satisfies AboutMetric[],
    highlights: [
        'Full-stack engineering across modern frontend, Python services, Node.js APIs, and databases',
        'AI and ML integration for interview scoring, anomaly detection, summarization, and assistant flows',
        'Performance-focused engineering using code splitting, caching, dynamic imports, and cleaner request paths',
        'Comfort with system design, collaboration, and maintainable architecture',
    ],
    skillGroups: [
        {
            title: 'Frontend',
            items: ['React', 'Next.js', 'TypeScript', 'JavaScript', 'Tailwind CSS', 'Redux Toolkit', 'Responsive UI', 'Accessibility'],
        },
        {
            title: 'Backend',
            items: ['Node.js', 'Express', 'Python', 'Flask', 'FastAPI', 'Django', 'REST APIs', 'GraphQL', 'JWT', 'OAuth'],
        },
        {
            title: 'Data & Cloud',
            items: ['MongoDB', 'PostgreSQL', 'MySQL', 'Redis', 'AWS', 'Firebase', 'Docker', 'Vercel', 'CI/CD', 'Linux'],
        },
        {
            title: 'AI / ML',
            items: ['TensorFlow', 'scikit-learn', 'OpenCV', 'Hugging Face', 'LLM integrations', 'Anomaly detection', 'Pandas', 'NumPy', "Langchain", "Lang-graph"],
        },
        {
            title: 'MS Office',
            items: ['Microsoft Excel', 'Microsoft Word', 'Microsoft PowerPoint'],
        },
    ] satisfies SkillGroup[],
    experience: [
        {
            company: 'DOI Software',
            role: 'Full-Stack Developer',
            timeline: 'Nov 2024 - Present',
            summary: 'Building academic journal platforms with a Next.js-first frontend, backend integrations, and strong emphasis on performance, SEO, and admin usability.',
            highlights: [
                'Shipped Lighthouse performance above 98 with SEO scores of 100.',
                'Refactored request pipelines and reduced API response time by roughly 35 percent.',
                'Improved dashboard and editorial workflows with an estimated 60 percent productivity gain.',
                'Shipped 13+ websites successfully.'
            ],
        },
        {
            company: 'wiZe AI (MyLamp)',
            role: 'Next.js Developer Intern',
            timeline: 'Dec 2024 - Aug 2025',
            summary: 'Contributing to an AI mock interviewer with code, speech, and confidence feedback inside a production-oriented interview flow.',
            highlights: [
                'Implemented interview modules tied to real-time AI service integrations.',
                'Reduced page load times by around 38 percent using dynamic imports and caching.',
                'Supported roughly 18 percent higher session retention after UX and performance improvements.',
            ],
        },
        {
            company: 'Regional Client, Asansol',
            role: 'Backend Developer',
            timeline: 'Aug 2023 - Oct 2023',
            summary: 'Built a Python and Flask backend for a public-sector job portal with automated scraping, normalized data models, and real-time listing updates.',
            highlights: [
                'Created Selenium-based ingestion automation for job updates.',
                'Designed MySQL schemas and REST endpoints for reliable data access.',
                'Supported about 22 percent higher engagement through fresher listings and targeted alerts.',
            ],
        },
    ] satisfies ExperienceItem[],
}

export const PORTFOLIO_PAGES: Record<string, PortfolioPageContent> = {
    'myworks': {
        eyebrow: 'Selected Work',
        title: 'A portfolio of shipped products, working systems, and hands-on engineering builds.',
        intro: 'This page highlights the work that best represents product thinking, implementation range, and delivery quality across frontend, backend, AI, and realtime systems.',
        purpose: 'Start here for a fast view of what has been built, the role played in each project, and the outcomes that make the work worth exploring further.',
        cardLabel: 'Project Cards',
        detailLabel: 'Individual Project Follow-Up',
        cards: [
            {
                id: 'doi-journals',
                name: 'Academic Journals Platform',
                description: 'High-performance research publishing platform with admin workflows and backend service integration.',
                duration: 'Nov 2024 - Present',
                role: 'Full-Stack Developer',
                teamSize: 'Distributed team',
                status: 'Production',
                stack: ['Next.js', 'React', 'Tailwind', 'Python', 'Node.js', 'REST APIs'],
                impact: 'Lighthouse 98+, SEO 100, and operational workflow gains of about 60 percent.',
                link:"https://www.fdrpjournals.org/"
            },
            {
                id: 'onepoint-prep',
                name: 'AI-Powered Interview Platform',
                description: 'Mock interview workflow with coding, speech, and AI-backed feedback for realistic practice sessions.',
                duration: 'Ongoing',
                role: 'Frontend / Full-Stack Integration',
                teamSize: 'Cross-functional product team',
                status: 'Production / Product build',
                stack: ['Next.js', 'React', 'Python APIs', 'Caching', 'AI services'],
                impact: 'Faster page load time and about 18 percent stronger session retention.',
            },
            {
                id: 'chat-app',
                name: 'Real-Time Chat Application',
                description: 'Room-based audio, video, and messaging system designed for low-latency communication.',
                duration: 'Personal / portfolio build',
                role: 'Full-Stack Developer',
                teamSize: 'Solo',
                status: 'Personal',
                stack: ['React', 'Next.js', 'Node.js', 'Socket.io', 'WebRTC', 'NoSQL DB'],
                impact: 'Sub-50ms messaging and stable multi-room communication flow.',
                link:"https://whatsapp-mauve.vercel.app/"
            },
            {
                id: 'medical-triage',
                name: 'Medical Triage Bot',
                description: 'Healthcare assistant prototype using ML-backed symptom analysis and recommendation workflows.',
                duration: 'Prototype',
                role: 'Full-Stack Engineer',
                teamSize: 'Solo',
                status: 'Proof of concept',
                stack: ['Python', 'Flask', 'NLP', 'ML models', 'Frontend UI'],
                impact: 'Demonstrated contextual symptom analysis for telemedicine-style flows.',
                link:"https://whatsapp-mauve.vercel.app/"
            },
            {
                id: 'vitals-detection',
                name: 'Vitals Abnormality Detection',
                description: 'Machine-learning pipeline for detecting anomalies in patient vital signs data.',
                duration: 'Prototype / research build',
                role: 'ML / Data Engineer',
                teamSize: 'Solo',
                status: 'Internal benchmarked prototype',
                stack: ['Python', 'TensorFlow', 'scikit-learn', 'Feature engineering'],
                impact: 'Reached around 99.5 percent accuracy on internal benchmarks.',
                link:"https://whatsapp-mauve.vercel.app/"
            },
            {
                id: 'ems-dashboard',
                name: 'EMS Dashboard',
                description: 'Multi-role CRM-style dashboard with alerts, filtering, and API-driven visualization flows.',
                duration: 'Client delivery',
                role: 'Frontend Developer',
                teamSize: 'Product team',
                status: 'Client system',
                stack: ['React', 'Redux Toolkit', 'REST APIs', 'Visualization UI', "laravel"],
                impact: 'Unified operational interface for complex emergency-management workflows.',
                link:"https://editorial.fdrpjournals.org/login"
            },
        ],
        deepDives: [
            {
                id: 'doi-journals',
                name: 'Academic Journals Platform',
                tagline: 'Performance-sensitive publishing infrastructure for global research workflows.',
                overview: 'This project centers on building large-scale journal platforms that need to be fast, discoverable, and easy for editorial teams to manage. The engineering challenge is not just rendering pages, but coordinating performance, SEO, admin experiences, and backend reliability.',
                problem: 'Research and publishing platforms often become slow, cluttered, and difficult to maintain when content, workflows, and integrations grow. The goal here was to deliver a cleaner system with strong discoverability and better internal efficiency.',
                decisions: [
                    'Used Next.js to support high-performance rendering and SEO-sensitive content delivery.',
                    'Applied code splitting, dynamic imports, and leaner request flows to keep the platform responsive.',
                    'Kept the frontend modular so admin and dashboard surfaces could evolve without bloating the whole system.',
                ],
                results: [
                    'Lighthouse performance above 98.',
                    'SEO benchmark at 100.',
                    'API latency reduced by about 35 percent.',
                ],
                blocks: [
                    {
                        label: 'Objectives',
                        items: ['Improve discoverability and technical SEO', 'Make admin workflows faster', 'Reduce latency in request-heavy paths'],
                    },
                    {
                        label: 'Key Features',
                        items: ['Editorial dashboards', 'Content workflows', 'Backend-integrated admin panels', 'Performance-sensitive public pages'],
                    },
                    {
                        label: 'Optimizations',
                        items: ['Dynamic imports', 'Bundle reduction', 'Request pipeline cleanup', 'Better render-path discipline'],
                    },
                ],
            },
            {
                id: 'onepoint-prep',
                name: 'AI-Powered Interview Platform',
                tagline: 'A live-feeling interview product connecting UI, AI feedback, and candidate analytics.',
                overview: 'This work combines product UX with asynchronous AI pipelines. Candidates interact with coding and speech modules while the interface stays responsive and feedback remains timely.',
                problem: 'Interview preparation platforms often feel static or delayed when AI analysis is involved. The challenge was to make the experience feel immediate while coordinating multiple backend evaluation services.',
                decisions: [
                    'Used Next.js for a fast UI shell and modular page composition.',
                    'Leaned on lazy loading and caching so heavier AI-related modules would not hurt initial page experience.',
                    'Designed the frontend to handle incremental feedback instead of waiting on entire backend cycles.',
                ],
                results: [
                    'Page load times reduced by about 38 percent.',
                    'Session retention improved by around 18 percent.',
                    'Delivered realistic coding and speech-feedback flows.',
                ],
                blocks: [
                    {
                        label: 'Objectives',
                        items: ['Make interview practice realistic', 'Support AI-backed code and speech evaluation', 'Keep the UI smooth under async workloads'],
                    },
                    {
                        label: 'Features',
                        items: ['Coding interface', 'Speech feedback', 'Progress tracking', 'Analytics-oriented result flows'],
                    },
                    {
                        label: 'Architecture Notes',
                        items: ['Next.js frontend', 'Python services for evaluation', 'REST API orchestration', 'Caching for repeat-heavy flows'],
                    },
                ],
            },
            {
                id: 'chat-app',
                name: 'Real-Time Chat Application',
                tagline: 'Low-latency messaging and media communication built around persistent connections.',
                overview: 'This project focuses on room-based communication with text, audio, and video. The main engineering task was selecting an architecture that could preserve immediacy while remaining understandable and scalable.',
                problem: 'Polling-based or request-response systems are a poor fit for live chat and media signaling. Users need near-instant updates and stable room behavior across concurrent sessions.',
                decisions: [
                    'Chose WebSockets through Socket.io because full-duplex communication was essential.',
                    'Used WebRTC for media transport while keeping signaling concerns separate on the server side.',
                    'Structured the frontend into focused modules for messages, room state, and media handling.',
                ],
                results: [
                    'Sub-50ms message latency.',
                    'Stable multi-room behavior under concurrent usage.',
                    'A reusable architecture pattern for future collaboration products.',
                ],
                blocks: [
                    {
                        label: 'Features',
                        items: ['Text chat', 'Room management', 'Audio/video support', 'Persistent message flow'],
                    },
                    {
                        label: 'Challenges',
                        items: ['State synchronization', 'Socket lifecycle handling', 'Media connection reliability'],
                    },
                    {
                        label: 'Solutions',
                        items: ['Socket keep-alive tuning', 'TURN/STUN support', 'Clear signaling boundaries', 'Focused React components'],
                    },
                ],
            },
        ],
    },
    'case-studies': {
        eyebrow: 'Case Studies',
        title: 'Deeper case studies that show how problems were framed, solved, and turned into real outcomes.',
        intro: 'These projects are presented as engineering stories, covering the challenge, the build approach, the technical decisions made, and the results delivered.',
        purpose: 'Use this page when you want the reasoning behind the work, not just the final interface or feature list.',
        cardLabel: 'Case Study Cards',
        detailLabel: 'Detailed Breakdown',
        cards: [
            {
                id: 'cs-chat',
                name: 'Real-Time Chat Application',
                description: 'Scalable WebRTC and Socket.io messaging with room-based communication and persistent low-latency updates.',
                duration: 'Portfolio project',
                role: 'Full-Stack Developer',
                teamSize: 'Solo',
                status: 'Personal / technical showcase',
                stack: ['React', 'Next.js', 'Node.js', 'Socket.io', 'WebRTC', 'Database'],
                impact: 'Delivered live messaging performance under 50ms with concurrent room support.',
            },
            {
                id: 'cs-wize',
                name: 'AI-Powered Interview Platform',
                description: 'AI-enhanced interview experience combining code analysis, voice feedback, and performance-focused frontend delivery.',
                duration: 'Ongoing',
                role: 'Next.js Developer / Integration Engineer',
                teamSize: 'Product + ML collaboration',
                status: 'Production',
                stack: ['Next.js', 'React', 'Python services', 'REST APIs', 'Caching', 'AI integrations'],
                impact: 'Improved retention and response experience while supporting complex AI workflows.',
            },
            {
                id: 'cs-explainer',
                name: 'Web Explainer AI Agent',
                description: 'LLM-assisted webpage summarization tool that fetches content, extracts context, and streams explanations back to users.',
                duration: 'Personal product',
                role: 'Full-Stack / AI Integration Engineer',
                teamSize: 'Solo',
                status: 'Live prototype',
                stack: ['Node.js', 'React', 'Next.js', 'LLM API', 'Streaming responses'],
                impact: 'Turned dense webpages into accessible, live-generated summaries with a public demo.',
            },
        ],
        deepDives: [
            {
                id: 'cs-chat',
                name: 'Real-Time Chat Application',
                tagline: 'Built for immediacy, concurrency, and clean separation between communication layers.',
                overview: 'The chat system is a strong engineering case study because the architecture choice mattered more than the UI alone. Real-time systems quickly expose weak assumptions around state, latency, and connection handling.',
                problem: 'The goal was to create a browser-based system for text, video, and audio communication in multiple rooms without the lag and awkwardness of request-response approaches.',
                decisions: [
                    'Used a persistent WebSocket model through Socket.io for instant bi-directional updates.',
                    'Combined WebRTC for media transport with server-based signaling to keep responsibilities clear.',
                    'Designed the frontend around separate modules like ChatRoom, MessageList, and VideoPlayer to reduce state complexity.',
                ],
                results: [
                    'Sub-50ms end-to-end message latency.',
                    'Stable room behavior for concurrent usage.',
                    'Reusable architecture principles for collaboration products.',
                ],
                blocks: [
                    {
                        label: 'Problem Statement',
                        items: ['Users need instant room-based communication', 'Polling is too slow and too wasteful', 'Audio and video require reliable signaling'],
                    },
                    {
                        label: 'Challenges & Solutions',
                        items: ['Challenge: state sync across sockets -> Solution: event-driven room state handling', 'Challenge: connection stability -> Solution: TURN/STUN support and keep-alive tuning'],
                    },
                    {
                        label: 'Scalability & Lessons',
                        items: ['Horizontal scaling readiness', 'Importance of separating signaling from media', 'Need for clear socket event contracts'],
                    },
                ],
            },
            {
                id: 'cs-wize',
                name: 'AI-Powered Interview Platform',
                tagline: 'An async AI workflow that still feels immediate to the user.',
                overview: 'This case study shows how product experience and backend intelligence have to work together. AI features are only useful when the frontend is structured to handle uncertainty, latency, and streaming-style updates gracefully.',
                problem: 'Candidates need interview practice that feels realistic and actionable, but AI scoring and speech analysis often introduce lag, unpredictability, and fragmented UX.',
                decisions: [
                    'Used Next.js for a modular, performance-conscious frontend.',
                    'Integrated AI-related services through APIs while keeping the UI resilient to partial or delayed results.',
                    'Applied lazy loading and caching to protect the user experience from heavy dependencies.',
                ],
                results: [
                    'About 38 percent faster page load times.',
                    'Roughly 18 percent better session retention.',
                    'A more lifelike interview flow with integrated code and speech feedback.',
                ],
                blocks: [
                    {
                        label: 'Objectives',
                        items: ['Improve realism of mock interviews', 'Blend code and voice feedback', 'Keep users engaged through faster interaction'],
                    },
                    {
                        label: 'Technical Decisions',
                        items: ['Frontend decoupled from evaluation latency', 'Caching for repeated flows', 'Dynamic imports for heavy interview modules'],
                    },
                    {
                        label: 'Future Improvements',
                        items: ['Richer analytics', 'More streaming-based feedback', 'Expanded AI evaluation coverage'],
                    },
                ],
            },
            {
                id: 'cs-explainer',
                name: 'Web Explainer AI Agent',
                tagline: 'A clean example of AI orchestration, content processing, and streamed UX.',
                overview: 'This project translates a common pain point into a focused AI product: understanding long or complex webpages quickly. It combines fetching, parsing, LLM orchestration, and response streaming in one clear workflow.',
                problem: 'People waste time manually reading dense pages just to extract the main idea. The project goal was to shorten that path while keeping the experience simple and fast.',
                decisions: [
                    'Built a routing layer that distinguishes URLs from direct user queries.',
                    'Used streamed responses so the interface feels live instead of blocked by model latency.',
                    'Handled noisy HTML and edge cases before sending context to the model layer.',
                ],
                results: [
                    'Working live prototype with public demo.',
                    'End-to-end AI workflow that turns raw pages into readable summaries.',
                    'A strong showcase of practical LLM product integration.',
                ],
                blocks: [
                    {
                        label: 'System Flow',
                        items: ['Input routing', 'Web content fetch and cleaning', 'LLM prompt flow', 'Streaming output back to UI'],
                    },
                    {
                        label: 'Optimizations',
                        items: ['Streaming responses', 'Content cleanup before inference', 'Structured markdown output'],
                    },
                    {
                        label: 'Lessons Learned',
                        items: ['Streaming improves perceived speed', 'Input preprocessing matters as much as the prompt', 'AI UX benefits from transparency and progressive output'],
                    },
                ],
            },
        ],
    },
    'system-architecture-design': {
        eyebrow: 'System Design',
        title: 'Architecture work focused on system design, data flow, scalability, and engineering tradeoffs.',
        intro: 'This section highlights how systems were structured behind the UI, including communication patterns, service boundaries, performance considerations, and future scaling direction.',
        purpose: 'Use this page to review technical decision-making, not just features, and to see how product requirements were translated into reliable system design.',
        cardLabel: 'Architecture Cards',
        detailLabel: 'Architecture Follow-Up',
        cards: [
            {
                id: 'arch-chat',
                name: 'Real-Time Chat Architecture',
                description: 'Client-server communication model using WebSockets for messaging and WebRTC for media exchange.',
                duration: 'Portfolio build',
                role: 'System designer + full-stack developer',
                teamSize: 'Solo',
                status: 'Working prototype',
                stack: ['React', 'Node.js', 'Socket.io', 'WebRTC', 'Database'],
                impact: 'Low-latency communication pattern suitable for real-time collaboration systems.',
            },
            {
                id: 'arch-interview',
                name: 'AI Interview Workflow',
                description: 'Frontend, API, and AI service pipeline designed to keep async evaluations usable in a live product.',
                duration: 'Product work',
                role: 'Frontend and integration engineer',
                teamSize: 'Cross-functional team',
                status: 'Production workflow',
                stack: ['Next.js', 'Python services', 'REST APIs', 'Caching', 'AI analysis services'],
                impact: 'Improved response feel and retention while supporting multiple AI-backed evaluation paths.',
            },
            {
                id: 'arch-doi',
                name: 'Performance-First Publishing Platform',
                description: 'SEO-sensitive and admin-heavy web platform shaped around render performance and efficient request paths.',
                duration: 'Ongoing',
                role: 'Full-stack developer',
                teamSize: 'Distributed team',
                status: 'Production',
                stack: ['Next.js', 'React', 'Python', 'Node.js', 'Admin workflows'],
                impact: '98+ Lighthouse, 100 SEO, and roughly 35 percent lower API latency.',
            },
        ],
        deepDives: [
            {
                id: 'arch-chat',
                name: 'Real-Time Chat Architecture',
                tagline: 'Persistent connections where immediacy matters more than simple request-response simplicity.',
                overview: 'This system follows a client-server model for signaling and state coordination, with WebRTC handling media exchange. It is a good example of using the right communication pattern for the problem instead of forcing everything through HTTP.',
                problem: 'Chat and live media break down quickly when the architecture is built around delayed or repetitive polling. The system needed to support room-based communication, fast broadcast, and stable peer connection setup.',
                decisions: [
                    'Adopted Socket.io for full-duplex messaging and room events.',
                    'Used WebRTC specifically for peer media exchange rather than routing all media through the app server.',
                    'Kept the design modular so signaling, room logic, and persistence remained understandable.',
                ],
                results: [
                    'Real-time responsiveness under live usage.',
                    'Clear separation between transport concerns.',
                    'A foundation that can scale into collaboration-oriented products.',
                ],
                blocks: [
                    {
                        label: 'Architecture Style',
                        items: ['Client-server signaling', 'Event-driven updates', 'Modular real-time services'],
                    },
                    {
                        label: 'Components',
                        items: ['Frontend room UI', 'Socket server', 'Media signaling layer', 'Database persistence'],
                    },
                    {
                        label: 'Data Flow',
                        items: ['User joins room', 'Socket connection established', 'Events broadcast to room', 'Messages stored and reflected in UI'],
                    },
                ],
            },
            {
                id: 'arch-interview',
                name: 'AI Interview Workflow',
                tagline: 'A layered product flow where asynchronous intelligence does not break the interface.',
                overview: 'The interview system combines a responsive frontend with multiple backend or AI evaluation services. The architecture needs to manage code execution, speech-related analysis, and feedback delivery without making the user feel blocked.',
                problem: 'AI pipelines are often slower and less predictable than normal CRUD flows. The system had to balance richer analysis with a product experience that still feels responsive and guided.',
                decisions: [
                    'Separated the interactive UI from the heavy evaluation processes.',
                    'Used caching and lazy loading to protect initial performance.',
                    'Designed the frontend to accept incremental or delayed results gracefully.',
                ],
                results: [
                    'Better response feel for interview flows.',
                    'Improved session retention.',
                    'An architecture that can evolve as evaluation services grow.',
                ],
                blocks: [
                    {
                        label: 'Architecture Style',
                        items: ['Client-server', 'Layered services', 'Async evaluation pipeline'],
                    },
                    {
                        label: 'API Flow',
                        items: ['Frontend submits code or speech context', 'Service evaluates input', 'Results returned to UI', 'State updates with progress and analytics'],
                    },
                    {
                        label: 'Key Decisions',
                        items: ['Next.js for modular delivery', 'Python services for analysis', 'Caching for repeat interactions', 'Deferred loading of heavier modules'],
                    },
                ],
            },
            {
                id: 'arch-doi',
                name: 'Performance-First Publishing Platform',
                tagline: 'A web platform architecture optimized for content discoverability and admin efficiency.',
                overview: 'This architecture case is less about exotic system patterns and more about strong engineering discipline: render performance, request efficiency, SEO quality, and maintainable admin surfaces.',
                problem: 'Content-heavy platforms often suffer from slow load times, bloated admin interfaces, and backend paths that become sluggish as the platform grows. The goal was to keep the system fast and usable on both public and internal sides.',
                decisions: [
                    'Used Next.js to align rendering strategy with performance and SEO goals.',
                    'Applied component modularity and dynamic loading to reduce bundle pressure.',
                    'Refined backend request paths to cut latency and support steadier operations.',
                ],
                results: [
                    '98+ Lighthouse performance.',
                    'SEO score of 100.',
                    'Roughly 35 percent API latency improvement.',
                ],
                blocks: [
                    {
                        label: 'Architecture Style',
                        items: ['Client-server', 'Modular frontend architecture', 'Service-integrated backend workflows'],
                    },
                    {
                        label: 'Optimizations',
                        items: ['Dynamic imports', 'Code splitting', 'Request-path cleanup', 'Better admin surface composition'],
                    },
                    {
                        label: 'Monitoring Focus',
                        items: ['Performance benchmarks', 'SEO checks', 'Operational workflow feedback', 'API responsiveness tracking'],
                    },
                ],
            },
        ],
    },
    'blog': {
        eyebrow: 'Writing',
        title: 'Engineering notes and practical writing drawn from real product and system work.',
        intro: 'This section is for sharing what was learned while building, optimizing, and scaling applications across frontend, backend, AI, and realtime workflows.',
        purpose: 'It gives additional context behind the portfolio by turning implementation experience into clear technical writing.',
        cardLabel: 'Writing Cards',
        detailLabel: 'Expanded Notes',
        cards: [
            {
                id: 'blog-react',
                name: 'React Performance Notes',
                description: 'Practical lessons from Lighthouse tuning, render cleanup, and dynamic loading decisions.',
                duration: 'Ongoing topic',
                role: 'Engineer writing from production work',
                teamSize: 'Solo author',
                status: 'Planned / expandable',
                stack: ['React', 'Next.js', 'Performance tooling'],
                impact: 'Connects portfolio outcomes to specific engineering practices.',
            },
            {
                id: 'blog-realtime',
                name: 'Real-Time Architecture Learnings',
                description: 'Why WebSockets, signaling boundaries, and streamed updates matter in interactive products.',
                duration: 'Ongoing topic',
                role: 'Engineer writing from project implementation',
                teamSize: 'Solo author',
                status: 'Planned / expandable',
                stack: ['Socket.io', 'WebRTC', 'Streaming UX'],
                impact: 'Turns project implementation into reusable engineering insight.',
            },
        ],
        deepDives: [
            {
                id: 'blog-react',
                name: 'React Performance Notes',
                tagline: 'Where portfolio results connect back to practical frontend engineering choices.',
                overview: 'This writing stream can unpack the exact habits behind better Lighthouse scores and smoother interfaces: smaller bundles, dynamic imports, state discipline, and cleaner render paths.',
                problem: 'Many portfolios show performance numbers without explaining what engineering choices produced them. This section can close that gap.',
                decisions: [
                    'Use examples directly from the journal and dashboard work.',
                    'Focus on practical changes rather than generic optimization advice.',
                    'Tie each article to measurable outcomes where possible.',
                ],
                results: [
                    'Turns performance claims into evidence-backed explanations.',
                    'Makes the writing section directly support the rest of the portfolio.',
                ],
                blocks: [
                    {
                        label: 'Possible Topics',
                        items: ['Dynamic imports', 'Render path cleanup', 'Caching strategy', 'Lighthouse improvement workflow'],
                    },
                ],
            },
            {
                id: 'blog-realtime',
                name: 'Real-Time Architecture Learnings',
                tagline: 'Turning project-specific system design into reusable explanations.',
                overview: 'This stream can document why persistent communication patterns, streamed feedback, and async UI handling matter when building interactive systems.',
                problem: 'Real-time and AI products often look simple on the surface but hide non-trivial design tradeoffs that are worth documenting clearly.',
                decisions: [
                    'Use chat and AI interview systems as anchor examples.',
                    'Explain tradeoffs rather than only tooling choices.',
                    'Keep the writing accessible to both recruiters and engineers.',
                ],
                results: [
                    'Strengthens the system-design side of the portfolio.',
                    'Creates continuity across case studies, architecture, and writing.',
                ],
                blocks: [
                    {
                        label: 'Possible Topics',
                        items: ['Why WebSockets fit chat', 'What streaming changes in AI UX', 'Managing async state without degrading UX'],
                    },
                ],
            },
        ],
    },
}
